import { create } from "zustand";
import { api } from "@/lib/api";
import type { User } from "@/types";

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isRestored: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  restoreSession: () => Promise<void>;
  testLogin: () => Promise<void>;
  setUser: (user: User | null) => void;
}

function normalizeUser(u: { id: string; email: string; providerConnected: boolean; providerType: string | null }): User {
  return {
    id: u.id,
    email: u.email,
    providerConnected: u.providerConnected,
    providerType: u.providerType as "gmail" | "outlook" | null,
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: false,
  isRestored: false,

  login: async (email, password) => {
    set({ isLoading: true });
    try {
      const data = await api.login(email, password);
      set({ user: normalizeUser(data.user), isLoading: false });
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  signup: async (email, password) => {
    set({ isLoading: true });
    try {
      const data = await api.signup(email, password);
      set({ user: normalizeUser({ ...data.user, providerConnected: false, providerType: null }), isLoading: false });
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  logout: async () => {
    await api.logout();
    set({ user: null });
  },

  restoreSession: async () => {
    try {
      const data = await api.session();
      set({ user: normalizeUser(data.user), isRestored: true });
    } catch {
      set({ user: null, isRestored: true });
    }
  },

  testLogin: async () => {
    set({ isLoading: true });
    try {
      const data = await api.testAccountLogin();
      set({ user: normalizeUser(data.user), isLoading: false });
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  setUser: (user) => set({ user }),
}));
